import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';
import { AccountDownloadsPolicies } from '../../../../../../content-security/downloads/gql/generated/fragments';
import { AccountRequirePassphrasePolicies } from '../../../../../../content-security/require-passphrase/gql/generated/fragments';
import { AccountShareAccessPolicies } from '../../../../../../content-security/share-access/gql/generated/fragments';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type ShareDefaultExpirationFragment = { __typename?: 'Account', id: string, resourceControlPolicies?: { __typename?: 'ResourceControlPolicies', shareDefaultExpirySeconds?: number | null } | null };

export type ShareDomainAccessEnabledFragment = { __typename?: 'Account', id: string, resourceControlPolicies?: { __typename?: 'ResourceControlPolicies', shareDomainAccessEnabled: boolean } | null };

export type AccessRequestsEnabledFragment = { __typename?: 'Account', id: string, resourceControlPolicies?: { __typename?: 'ResourceControlPolicies', accessRequestsEnabled?: boolean | null } | null };

export type AccountAllowedDomainsFragment = { __typename?: 'Account', id: string, allowedDomains?: { __typename?: 'AllowedDomainConnection', nodes: Array<{ __typename?: 'AllowedDomain', name: string }> } | null };

export type AccountAllowedRedirectDomainsFragment = { __typename?: 'Account', id: string, allowedRedirectDomains?: { __typename?: 'AllowedRedirectDomainConnection', nodes: Array<{ __typename?: 'AllowedRedirectDomain', name: string }> } | null };

export type AccountResourceControlPoliciesFragment = { __typename?: 'Account', id: string, resourceControlPolicies?: { __typename?: 'ResourceControlPolicies', shareDefaultExpirySeconds?: number | null, shareDomainAccessEnabled: boolean, accessRequestsEnabled?: boolean | null, downloads?: { __typename?: 'DownloadsSettings', shareDownloadingEnabled?: boolean | null, adminCanEnableInShares?: boolean | null, fullAccessCanEnableInShares?: boolean | null, editAndShareCanEnableInShares?: boolean | null, adminInternal?: boolean | null, fullAccessInternal?: boolean | null, editAndShareInternal?: boolean | null } | null, sharePassphrase?: { __typename?: 'SharePassphraseSettings', required?: boolean | null, adminCanDisableInShares?: boolean | null, fullAccessCanDisableInShares?: boolean | null, editAndShareCanDisableInShares?: boolean | null } | null, shareAccess?: { __typename?: 'ShareAccessSettings', default?: Types.ShareAccess | null, minimum?: Types.ShareAccess | null, adminCanDisableInShares?: boolean | null, fullAccessCanDisableInShares?: boolean | null, editAndShareCanDisableInShares?: boolean | null } | null } | null };

export const AccountAllowedDomains = /*#__PURE__*/ gql`
    fragment AccountAllowedDomains on Account {
  id
  allowedDomains(page: {first: 20}) {
    nodes {
      name
    }
  }
}
    ` as unknown as DocumentNode<AccountAllowedDomainsFragment, unknown>;
export const AccountAllowedRedirectDomains = /*#__PURE__*/ gql`
    fragment AccountAllowedRedirectDomains on Account {
  id
  allowedRedirectDomains(page: {first: 20}) {
    nodes {
      name
    }
  }
}
    ` as unknown as DocumentNode<AccountAllowedRedirectDomainsFragment, unknown>;
export const ShareDefaultExpiration = /*#__PURE__*/ gql`
    fragment ShareDefaultExpiration on Account {
  id
  resourceControlPolicies {
    shareDefaultExpirySeconds
  }
}
    ` as unknown as DocumentNode<ShareDefaultExpirationFragment, unknown>;
export const ShareDomainAccessEnabled = /*#__PURE__*/ gql`
    fragment ShareDomainAccessEnabled on Account {
  id
  resourceControlPolicies {
    shareDomainAccessEnabled
  }
}
    ` as unknown as DocumentNode<ShareDomainAccessEnabledFragment, unknown>;
export const AccessRequestsEnabled = /*#__PURE__*/ gql`
    fragment AccessRequestsEnabled on Account {
  id
  resourceControlPolicies {
    accessRequestsEnabled
  }
}
    ` as unknown as DocumentNode<AccessRequestsEnabledFragment, unknown>;
export const AccountResourceControlPolicies = /*#__PURE__*/ gql`
    fragment AccountResourceControlPolicies on Account {
  ...ShareDefaultExpiration
  ...ShareDomainAccessEnabled
  ...AccessRequestsEnabled
  ...AccountDownloadsPolicies
  ...AccountRequirePassphrasePolicies
  ...AccountShareAccessPolicies
}
    ${ShareDefaultExpiration}
${ShareDomainAccessEnabled}
${AccessRequestsEnabled}
${AccountDownloadsPolicies}
${AccountRequirePassphrasePolicies}
${AccountShareAccessPolicies}` as unknown as DocumentNode<AccountResourceControlPoliciesFragment, unknown>;

        

        
export const useShareDefaultExpirationFragment = (
  input: string | Omit<UseFragmentOptions<ShareDefaultExpirationFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<ShareDefaultExpirationFragment>({
    fragment: ShareDefaultExpiration,
    fragmentName: 'ShareDefaultExpiration',
    ...options,
  });
};
  
        
        

        
export const useShareDomainAccessEnabledFragment = (
  input: string | Omit<UseFragmentOptions<ShareDomainAccessEnabledFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<ShareDomainAccessEnabledFragment>({
    fragment: ShareDomainAccessEnabled,
    fragmentName: 'ShareDomainAccessEnabled',
    ...options,
  });
};
  
        
        

        
export const useAccessRequestsEnabledFragment = (
  input: string | Omit<UseFragmentOptions<AccessRequestsEnabledFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccessRequestsEnabledFragment>({
    fragment: AccessRequestsEnabled,
    fragmentName: 'AccessRequestsEnabled',
    ...options,
  });
};
  
        
        

        
export const useAccountAllowedDomainsFragment = (
  input: string | Omit<UseFragmentOptions<AccountAllowedDomainsFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountAllowedDomainsFragment>({
    fragment: AccountAllowedDomains,
    fragmentName: 'AccountAllowedDomains',
    ...options,
  });
};
  
        
        

        
export const useAccountAllowedRedirectDomainsFragment = (
  input: string | Omit<UseFragmentOptions<AccountAllowedRedirectDomainsFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountAllowedRedirectDomainsFragment>({
    fragment: AccountAllowedRedirectDomains,
    fragmentName: 'AccountAllowedRedirectDomains',
    ...options,
  });
};
  
        
        

        
export const useAccountResourceControlPoliciesFragment = (
  input: string | Omit<UseFragmentOptions<AccountResourceControlPoliciesFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountResourceControlPoliciesFragment>({
    fragment: AccountResourceControlPolicies,
    fragmentName: 'AccountResourceControlPolicies',
    ...options,
  });
};
  
        